import React, { useRef, useState } from 'react';
import '../common.scss';
import './style.scss';
import { getSellComissions, SellCommissions } from 'shared';
import TextField from '../../../controls/textField';
import Button from '../../../controls/button';
import { stringToNumberOrFalse } from '../../../../utils/utils';
import useCream from '../../../../contexts/cream';

interface CreamData {
  value: string;
  error: string | undefined;
  empty: boolean;
  calcs: SellCommissions;
}

const errors = {
  notEnough: 'Minimal withdrawal is 100 cream',
  overflow: 'You have no such amount of cream',
  generic: 'Incorrect cream amount'
};

const SellView = () => {
  const { balance, balanceLoading, sellCream } = useCream();
  const [fieldValue, setFieldValue] = useState<string>('');
  const [cream, setCream] = useState<CreamData>({
    value: '',
    error: errors.notEnough,
    empty: true,
    calcs: getSellComissions(0)
  });
  const [sellLoading, setSellLoading] = useState<boolean>(false);
  const debounceTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  type VFailure = { error: string; numeric: number };
  type VSuccess = { numeric: number };

  const validate = (value: string): VFailure | VSuccess => {
    const numeric = stringToNumberOrFalse(value);
    if (numeric === false) {
      return { error: errors.generic, numeric: 0 };
    }
    if (numeric < 100) {
      return { error: errors.notEnough, numeric };
    }
    if (numeric > balance) {
      return { error: errors.overflow, numeric };
    }
    return { numeric };
  };

  const handleChange = (newAmount: string) => {
    if (sellLoading) return;
    setFieldValue(newAmount);
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      const resolve = (error: string | undefined, calcsArg: number) => {
        setCream(() => ({
          value: newAmount,
          empty: newAmount === '',
          error,
          calcs: getSellComissions(calcsArg)
        }));
      };
      const result = validate(newAmount);
      if ('error' in result) {
        return resolve(result.error, result.numeric);
      }
      resolve(undefined, result.numeric);
    }, 200);
  };

  const handleSellClick = (clone: CreamData) => {
    setSellLoading(true);
    const validation = validate(clone.value);
    if ('error' in validation) {
      return setSellLoading(false);
    }
    sellCream(
      validation.numeric,
      () => handleChange(''),
      () => setSellLoading(false)
    );
  };

  return (
    <div className='creamshop-view creamshop-sell'>
      <h1 className='creamshop-view-title'>Sell $CREAM</h1>
      <div className='creamshop-view-textfield-wrapper'>
        <TextField
          value={fieldValue}
          setValue={(newAmount) => handleChange(newAmount)}
          type='price'
          className='creamshop-view-textfield'
          autoFocus
          disabled={sellLoading}
          symbolsLimit={6}
          valid={!cream.error || cream.empty}
        />
        {!cream.empty && <span className='body-2'>$CREAM</span>}
      </div>
      <div className='creamshop-sell-hint'>
        <h4 className='body-1'>NIFTY FEE: 10%</h4>
        <h4>
          {!cream.empty && cream.error}
          {!cream.error && (
            <>
              Total Withdraw Amount: {cream.calcs.creamTotal} $CREAM ={' '}
              {cream.calcs.total} {process.env.REACT_APP_CURRENCY_SYMBOL}
            </>
          )}
          &nbsp;
        </h4>
      </div>
      <Button
        className='creamshop-view-button'
        color='secondary'
        disabled={!!cream.error || cream.empty || balanceLoading}
        loading={sellLoading}
        onClick={() => handleSellClick({ ...cream })}
      >
        Withdraw
      </Button>
    </div>
  );
};

export default SellView;
